import type { Accessor, ComponentProps, JSX } from "solid-js";
import { createMemo, For, Match, Show, Switch, mergeProps } from "solid-js";
import type { ExtendableInputProps } from "../inputs/Input";
import Input from "../inputs/Input";
import {
  createListCollection,
  SelectContent,
  SelectControl,
  SelectItem,
  SelectItemGroup,
  SelectItemGroupLabel,
  SelectPositioner,
  SelectRoot,
  SelectTrigger,
} from "@ark-ui/solid";
import { isEmpty, isEqual, isNil } from "lodash-es";
import { useTranslation } from "../../state/i18n";
import ImageListItem from "./listItems/ImageListItem";
import TextListItem from "./listItems/TextListItem";
import IconListItem from "./listItems/IconListItem";
import MenuEmptyState from "./MenuEmptyState";
import SingleSelectTrigger from "./triggers/SingleSelectTrigger";
import CompactSingleSelectTrigger from "./triggers/CompactSingleSelectTrigger";
import type { SelectOption } from "../../types/Menu";
import { narrow } from "../../utils/solidUtils";
import PillSingleSelectTrigger from "./triggers/PillSingleSelectTrigger";
import AvatarListItem from "./listItems/AvatarListItem";
import DynamicValueListItem from "./listItems/DynamicValueListItem";

type SelectMenuProps<T> = {
  ariaLabel: string;
  options: SelectOption<T>[];
  value?: T | null;
  onSelect: (value: T) => void;
  sameWidth: boolean;
  Trigger: (props: {
    triggerProps: ComponentProps<"button">;
    selectedOption: Accessor<SelectOption<T> | undefined>;
  }) => JSX.Element;
  disabled?: boolean;
};

const SelectMenu = <T,>(rawProps: SelectMenuProps<T>) => {
  const props = mergeProps({ disabled: false }, rawProps);
  const { t } = useTranslation();

  const selectedOption = createMemo(() => {
    if (isEmpty(props.options)) {
      return undefined;
    }
    return props.options.find((option) => !isNil(option) && isEqual(option.value, props.value));
  });

  const isGrouped = createMemo(() => props.options.some((option) => !isNil(option.group)));

  const collection = createMemo(() =>
    createListCollection({
      items: props.options,
      itemToValue: (item) => JSON.stringify(item.value),
      itemToString: (item) => item.label,
      groupBy: isGrouped() ? (item) => item.group ?? "" : undefined,
    }),
  );

  const renderOption = (option: SelectOption<T>) => (
    <SelectItem
      item={option}
      asChild={(arkProps) => (
        <Switch>
          <Match when={narrow(option, (o) => o.variant === "avatar")}>
            {(o) => <AvatarListItem htmlProps={arkProps()} {...o()} />}
          </Match>
          <Match when={narrow(option, (o) => o.variant === "image")}>
            {(o) => <ImageListItem htmlProps={arkProps()} {...o()} />}
          </Match>
          <Match when={narrow(option, (o) => o.variant === "icon")}>
            {(o) => <IconListItem htmlProps={arkProps()} {...o()} />}
          </Match>
          <Match when={narrow(option, (o) => o.variant === "text")}>
            {(o) => <TextListItem htmlProps={arkProps()} {...o()} />}
          </Match>
          <Match when={narrow(option, (o) => o.variant === "dynamicValue")}>
            {(o) => <DynamicValueListItem htmlProps={arkProps()} {...o()} />}
          </Match>
        </Switch>
      )}
    />
  );

  return (
    <SelectRoot
      collection={collection()}
      value={!isNil(selectedOption()) ? [JSON.stringify(selectedOption()?.value)] : []}
      onValueChange={(option) => props.onSelect(option.items[0].value as T)}
      positioning={{ placement: "bottom-start", gutter: 4, sameWidth: props.sameWidth }}
      closeOnSelect
      disabled={props.disabled}
    >
      <SelectControl>
        <SelectTrigger
          disabled={props.disabled}
          asChild={(arkProps) => <props.Trigger triggerProps={arkProps()} selectedOption={selectedOption} />}
        />
      </SelectControl>

      <SelectPositioner>
        <SelectContent
          aria-label={props.ariaLabel}
          class="z-50 max-h-86 overflow-y-auto rounded-sm bg-surface-default p-1 shadow-elevation-light-3 outline-none"
          style={
            !props.sameWidth
              ? { "min-width": "min(var(--reference-width), 42.5rem)", "max-width": "42.5rem" }
              : undefined
          }
        >
          <Switch>
            <Match when={isEmpty(props.options)}>
              <MenuEmptyState text={t(($) => $.SELECT_EMPTY_STATE_TITLE)} />
            </Match>
            <Match when={isGrouped()}>
              <For each={collection().group()}>
                {([groupKey, items]) => (
                  <SelectItemGroup>
                    <Show when={!isEmpty(groupKey)}>
                      <SelectItemGroupLabel class="px-3 pt-2 pb-0.5 text-header-sm text-secondary">
                        {groupKey}
                      </SelectItemGroupLabel>
                    </Show>
                    <For each={items}>{(option) => renderOption(option)}</For>
                  </SelectItemGroup>
                )}
              </For>
            </Match>
            <Match when={!isGrouped()}>
              <For each={props.options}>{(option) => renderOption(option)}</For>
            </Match>
          </Switch>
        </SelectContent>
      </SelectPositioner>
    </SelectRoot>
  );
};

type SingleSelectProps<T> = Pick<
  SelectMenuProps<T>,
  "ariaLabel" | "options" | "value" | "onSelect" | "sameWidth" | "disabled"
> & {
  placeholder?: string;
  onClear?: () => void;
  hasError: boolean;
};

export const SingleSelect = <T,>(rawProps: SingleSelectProps<T>) => {
  const props = mergeProps({ disabled: false }, rawProps);
  const showClearButton = createMemo(() => !isEmpty(props.value) && !isNil(props.onClear) && !props.disabled);

  return (
    <SelectMenu<T>
      ariaLabel={props.ariaLabel}
      options={props.options}
      value={props.value}
      onSelect={props.onSelect}
      sameWidth={props.sameWidth}
      disabled={props.disabled}
      Trigger={({ triggerProps: arkProps, selectedOption }) => (
        <SingleSelectTrigger<T>
          ariaLabel={props.ariaLabel}
          value={selectedOption()}
          withClearButton={showClearButton()}
          onClear={props.onClear}
          placeholder={props.placeholder}
          hasError={props.hasError}
          disabled={props.disabled}
          arkProps={arkProps}
        />
      )}
    />
  );
};

export type SingleSelectInputProps<T> = Omit<SingleSelectProps<T>, "ariaLabel" | "hasError"> & ExtendableInputProps;

export const SingleSelectInput = <T,>(props: SingleSelectInputProps<T>) => (
  <Input required={props.required} errorMessage={props.errorMessage} info={props.info} label={props.label}>
    {() => <SingleSelect<T> ariaLabel={props.label} {...props} hasError={!isNil(props.errorMessage)} />}
  </Input>
);

type CompactSingleSelectProps<T> = Pick<
  SelectMenuProps<T>,
  "ariaLabel" | "options" | "value" | "onSelect" | "sameWidth" | "disabled"
> & {
  placeholder?: string;
};

export const CompactSingleSelect = <T,>(props: CompactSingleSelectProps<T>) => (
  <SelectMenu
    ariaLabel={props.ariaLabel}
    options={props.options}
    value={props.value}
    onSelect={props.onSelect}
    sameWidth={false}
    disabled={props.disabled}
    Trigger={({ triggerProps, selectedOption }) => (
      <CompactSingleSelectTrigger
        ariaLabel={props.ariaLabel}
        label={selectedOption()?.label}
        placeholder={props.placeholder}
        disabled={props.disabled}
        arkProps={triggerProps}
      />
    )}
  />
);

export type PillSingleSelectProps<T> = { class?: string } & Pick<
  SelectMenuProps<T>,
  "ariaLabel" | "options" | "value" | "onSelect" | "disabled"
>;

export const PillSingleSelect = <T,>(props: PillSingleSelectProps<T>) => (
  <SelectMenu
    ariaLabel={props.ariaLabel}
    onSelect={props.onSelect}
    options={props.options}
    sameWidth={false}
    value={props.value}
    disabled={props.disabled}
    Trigger={({ triggerProps, selectedOption }) => (
      <PillSingleSelectTrigger
        ariaLabel={props.ariaLabel}
        value={selectedOption()}
        disabled={props.disabled}
        arkProps={triggerProps}
        class={props.class}
      />
    )}
  />
);
